import { FC, useState } from "react";

import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { canTranslateForms, getOppositeLocale, Locales, OrganizationCountry } from "@simplyk/common";
import { Control, FieldErrors, useWatch } from "react-hook-form";

import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { DashboardTextField } from "@/dashboard-design-system/TextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

interface QbDropdownOption {
  id: string;
  name: string;
}

export interface FundFormData {
  name: string;
  code: string;
  translatedName: string;
  qbAccountId: string;
  qbClassId: string;
  qbProductServiceId: string;
}

interface FundFormFieldsProps {
  control: Control<FundFormData>;
  errors: FieldErrors<FundFormData>;
  description?: string;
  locale: Locales;
  organizationCountry?: OrganizationCountry;
  initialShowTranslation?: boolean;
  onTranslationRemoved?: () => void;
  hasQuickBooks?: boolean;
  qbIncomeAccounts?: QbDropdownOption[];
  qbClasses?: QbDropdownOption[];
  qbProductsAndServices?: QbDropdownOption[];
}

export const FundFormFields: FC<FundFormFieldsProps> = ({
  control,
  errors,
  description,
  locale,
  organizationCountry,
  initialShowTranslation = false,
  onTranslationRemoved,
  hasQuickBooks = false,
  qbIncomeAccounts = [],
  qbClasses = [],
  qbProductsAndServices = [],
}) => {
  const { t } = useTranslate();
  const [showTranslation, setShowTranslation] = useState(initialShowTranslation);

  const canTranslate = organizationCountry ? canTranslateForms(organizationCountry) : false;
  const secondaryLocale = organizationCountry ? getOppositeLocale({ organizationCountry, locale }) : undefined;

  const primaryLocaleLabel = t("common", `locale.${locale}`);
  const secondaryLocaleLabel = secondaryLocale ? t("common", `locale.${secondaryLocale}`) : "";

  const qbAccountId = useWatch({ control, name: "qbAccountId" });
  const showQbClassAndProduct = Boolean(qbAccountId);

  const handleRemoveTranslation = () => {
    setShowTranslation(false);
    onTranslationRemoved?.();
  };

  return (
    <Stack spacing={2}>
      {description && (
        <DashboardTypography variant="body2" vibe="text-neutral-quiet">
          {description}
        </DashboardTypography>
      )}
      <Stack spacing={0.5}>
        <DashboardTextField
          size="medium"
          control={control}
          name="name"
          label={
            canTranslate
              ? t("dashboard", "funds.form.fundNameWithLocale", { locale: primaryLocaleLabel })
              : t("dashboard", "funds.create.fundName")
          }
          placeholder={t("dashboard", "funds.create.fundNamePlaceholder")}
          errorMessage={errors["name"]?.message}
          rules={[isRequired(t("dashboard", "funds.create.fundNameRequired"))]}
        />
        {canTranslate && !showTranslation && (
          <DashboardLink vibe="neutral" variant="caption" onClick={() => setShowTranslation(true)}>
            {t("dashboard", "funds.form.translateFundName")}
          </DashboardLink>
        )}
      </Stack>
      {canTranslate && showTranslation && (
        <Stack spacing={0.5}>
          <DashboardTextField
            size="medium"
            control={control}
            name="translatedName"
            label={t("dashboard", "funds.form.fundNameWithLocale", { locale: secondaryLocaleLabel })}
            placeholder={t("dashboard", "funds.create.fundNamePlaceholder")}
            errorMessage={errors["translatedName"]?.message}
            rules={[isRequired(t("dashboard", "funds.create.fundNameRequired"))]}
          />
          <DashboardLink vibe="neutral" variant="caption" onClick={handleRemoveTranslation}>
            {t("dashboard", "funds.form.removeTranslation")}
          </DashboardLink>
        </Stack>
      )}
      <DashboardTextField
        size="medium"
        control={control}
        name="code"
        label={t("dashboard", "funds.create.fundCode")}
        placeholder={t("dashboard", "funds.create.fundCodePlaceholder")}
        errorMessage={errors["code"]?.message}
        helperText={
          <DashboardTypography variant="caption" vibe="text-neutral-supershy">
            {t("dashboard", "settings.bank.funds.fundCodeDescription")}
          </DashboardTypography>
        }
        headerRight={
          <DashboardTypography variant="caption" vibe="text-neutral-quiet">
            {t("dashboard", "optional")}
          </DashboardTypography>
        }
      />
      {hasQuickBooks && (
        <Stack spacing={2}>
          <DashboardSelect
            control={control}
            name="qbAccountId"
            label={t("dashboard", "quickbooks.fundModal.qbAccount")}
            headerRight={
              <DashboardTypography variant="caption" vibe="text-neutral-quiet">
                {t("dashboard", "optional")}
              </DashboardTypography>
            }
            size="medium"
          >
            {qbIncomeAccounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </DashboardSelect>
          {showQbClassAndProduct && qbClasses.length > 0 && (
            <DashboardSelect
              control={control}
              name="qbClassId"
              label={t("dashboard", "quickbooks.fundModal.qbClass")}
              headerRight={
                <DashboardTypography variant="caption" vibe="text-neutral-quiet">
                  {t("dashboard", "optional")}
                </DashboardTypography>
              }
              size="medium"
            >
              {qbClasses.map((cls) => (
                <MenuItem key={cls.id} value={cls.id}>
                  {cls.name}
                </MenuItem>
              ))}
            </DashboardSelect>
          )}
          {showQbClassAndProduct && qbProductsAndServices.length > 0 && (
            <DashboardSelect
              control={control}
              name="qbProductServiceId"
              label={t("dashboard", "quickbooks.fundModal.qbProductService")}
              headerRight={
                <DashboardTypography variant="caption" vibe="text-neutral-quiet">
                  {t("dashboard", "optional")}
                </DashboardTypography>
              }
              size="medium"
            >
              {qbProductsAndServices.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </DashboardSelect>
          )}
        </Stack>
      )}
    </Stack>
  );
};
